import { useLocation, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    console.error('404 Error: User attempted to access non-existent route:', location.pathname);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-charcoal flex items-center justify-center relative overflow-hidden">
      {/* Decorative gold line accents */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-secondary/30 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-secondary/30 to-transparent" />
      <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-secondary/10 to-transparent" />
      <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-transparent via-secondary/10 to-transparent" />

      <div className="container max-w-2xl text-center px-6 relative z-10">
        {/* Large 404 number */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <h1 className="text-[10rem] sm:text-[14rem] font-display leading-none tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-secondary/80 to-secondary/10 select-none">
            404
          </h1>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="-mt-8 sm:-mt-12"
        >
          <div className="w-16 h-px bg-secondary/40 mx-auto mb-8" />

          <h2 className="text-2xl sm:text-3xl font-display text-primary-foreground mb-4">
            Page Not Found
          </h2>

          <p className="text-primary-foreground/40 leading-relaxed mb-10 max-w-md mx-auto">
            The page you're looking for doesn't exist or has been moved. 
            Let us guide you back to solid ground.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              asChild
              className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-semibold px-8 py-3 h-auto"
            >
              <Link to="/">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Link>
            </Button>

            <Button
              asChild
              variant="outline"
              className="border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/5 hover:border-secondary/40 px-8 py-3 h-auto"
            >
              <Link to="/en/contact">
                <Search className="h-4 w-4" />
                Contact Us
              </Link>
            </Button>
          </div>
        </motion.div>

        {/* Subtle path display */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-16 text-primary-foreground/15 text-xs font-mono tracking-wide"
        >
          {location.pathname}
        </motion.p>
      </div>
    </div>
  );
};

export default NotFound;
